/**
 * Cross-tab coordination for multiple Agent Desktop / widget iframes
 * sharing the same origin (localStorage is shared; sessionStorage is not).
 */

const PREFIX = 'xella:call:';
const CLAIM_TTL_MS = 8 * 60 * 60 * 1000; // match connector session sliding window

function createId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getTabId(): string {
  const key = `${PREFIX}tab-id`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) {
      return existing;
    }
    const created = createId();
    sessionStorage.setItem(key, created);
    return created;
  } catch {
    return createId();
  }
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Embedded contexts may block storage — callers fall back to in-memory behavior.
  }
}

function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** Shared callTraceId so multiple widgets append to one timeline. */
export function getOrCreateSharedCallTraceId(cxoneContactId: string): string {
  const key = `${PREFIX}trace:${cxoneContactId}`;
  const existing = readJson<{ id: string }>(key);
  if (existing?.id) {
    return existing.id;
  }
  const id = createId();
  writeJson(key, { id, at: Date.now() });
  return id;
}

export function readSharedCallTraceId(cxoneContactId: string): string | null {
  return readJson<{ id: string }>(`${PREFIX}trace:${cxoneContactId}`)?.id ?? null;
}

/** Stable ExternalReferenceID shared across widgets for the same CXone contact.
 * SAP CTI requires up to 35 alphanumeric chars with no dashes (KBA 3481409).
 */
export function toSapExternalReferenceId(seed: string): string {
  const alphanumeric = seed.replace(/[^a-zA-Z0-9]/g, '');
  const withPrefix = alphanumeric.toUpperCase().startsWith('CALL')
    ? alphanumeric
    : `CALL${alphanumeric}`;
  return withPrefix.slice(0, 35);
}

export function getOrCreateSharedReferenceId(cxoneContactId: string): string {
  const key = `${PREFIX}ref:${cxoneContactId}`;
  const existing = readJson<{ id: string }>(key);
  if (existing?.id) {
    return existing.id;
  }
  const id = toSapExternalReferenceId(cxoneContactId);
  writeJson(key, { id, at: Date.now() });
  return id;
}

/**
 * Claim a once-per-call action (call-log event, disposition write, ACCEPT).
 * Returns false when any tab (including this one) already claimed it within TTL.
 */
export function claimOnce(scopeKey: string, ttlMs: number = CLAIM_TTL_MS): boolean {
  const key = `${PREFIX}claim:${scopeKey}`;
  const tabId = getTabId();
  const existing = readJson<{ owner: string; at: number }>(key);
  if (existing && Date.now() - existing.at < ttlMs) {
    return false;
  }

  const payload = { owner: tabId, at: Date.now() };
  writeJson(key, payload);

  // Best-effort race check — another tab may have written between read and write.
  const confirmed = readJson<{ owner: string; at: number }>(key);
  return !!confirmed && confirmed.owner === tabId && confirmed.at === payload.at;
}

export function releaseClaimsForContact(cxoneContactId: string): void {
  const contactId = cxoneContactId.trim();
  if (!contactId) {
    return;
  }

  removeKey(`${PREFIX}trace:${contactId}`);
  removeKey(`${PREFIX}ref:${contactId}`);

  const claimPrefix = `${PREFIX}claim:`;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(claimPrefix) && key.includes(contactId)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach(removeKey);
  } catch {
    // ignore
  }
}

/**
 * Run async work only if this tab can claim the lock.
 * Prefers navigator.locks when available; falls back to localStorage claim.
 */
export async function runExclusive(
  scopeKey: string,
  work: () => Promise<void>
): Promise<'ran' | 'skipped'> {
  const lockName = `${PREFIX}lock:${scopeKey}`;

  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    let ran = false;
    await navigator.locks.request(lockName, { ifAvailable: true }, async (lock) => {
      if (!lock) {
        return;
      }
      if (!claimOnce(scopeKey)) {
        return;
      }
      ran = true;
      await work();
    });
    return ran ? 'ran' : 'skipped';
  }

  if (!claimOnce(scopeKey)) {
    return 'skipped';
  }
  await work();
  return 'ran';
}
